import isEqual from 'lodash/isEqual';
import type {
  AutoCalibrationRule,
  AutoCalibrationRulePeriod,
  CalibrationEventType,
  DeltaDirection,
  RatingScale,
  RatingTarget,
} from 'types';
import { formatIntegerList } from 'utils/util/util';

// The rule types live in types.ts so that neither Performance.tsx nor
// Campaign.tsx has to import this module to reference them.
export type {
  AutoCalibrationConditionType,
  AutoCalibrationRule,
  AutoCalibrationRuleCondition,
  AutoCalibrationRulePeriod,
  CalibrationEventType,
  CalibrationPeriodType,
  ConsecutiveCondition,
  DeltaCondition,
  DeltaDirection,
  EventCondition,
  ManagerRatingsCondition,
  OnaCondition,
  OnaCounter,
  RatingTarget,
  ThresholdOperator,
} from 'types';

export interface PreCompiledAutoCalibrationRule extends AutoCalibrationRule {
  compiledTemplate: string;
  compiledTemplateVariableNames: string[];
  // Decided once here so the per-row fill stays a single string replace
  // for every rule that does not mention the manager.
  usesManagerPlaceholder: boolean;
}

export interface CalibrationFlagNames {
  person: string;
  manager: string;
}

export const NAME_PLACEHOLDER = 'name';
export const MANAGER_PLACEHOLDER = 'manager';
// Filled per person on the dashboard rather than at compile time.
export const PERSON_PLACEHOLDERS = [NAME_PLACEHOLDER, MANAGER_PLACEHOLDER];

// Stored enum values are rendered through these so the flag text reads
// "up or down" and "1 cycle" rather than the raw "both" and "1 cycles".
// Any formatter left out falls back to the stored value.
export interface CalibrationPlaceholderFormatters {
  direction?: (direction: DeltaDirection) => string;
  event?: (event: CalibrationEventType) => string;
  period?: (period: AutoCalibrationRulePeriod) => string;
}

// True when a save changed the stored rules, which is what triggers the
// backend to recompute the cached calibration flags. A missing list and an
// empty one are both "no rules" for this purpose.
export const haveAutoCalibrationRulesChanged = (
  previousRules: AutoCalibrationRule[] | undefined,
  nextRules: AutoCalibrationRule[] | undefined
): boolean => !isEqual(previousRules ?? [], nextRules ?? []);

const REGEX_VARIABLE = /{([^}]*)}/g;

export const isCalibrationRuleEnabled = (rule: AutoCalibrationRule): boolean =>
  rule.enabled !== false;

export const getTemplatePlaceholders = (template: string): string[] =>
  Array.from(template.matchAll(REGEX_VARIABLE), (match) => match[1]);

const buildRatingsString = (
  target: RatingTarget | undefined,
  ratingScale: RatingScale
) => {
  if (!target || !ratingScale?.length) {
    return '';
  }

  const average = ratingScale.reduce((a, b) => a + b, 0) / ratingScale.length;
  if (target === 'positive') {
    return formatIntegerList(ratingScale.filter((r) => r > average));
  } else if (target === 'negative') {
    return formatIntegerList(ratingScale.filter((r) => r < average));
  } else if (target === 'neutral') {
    return formatIntegerList(ratingScale.filter((r) => r === average));
  } else if (Array.isArray(target)) {
    return formatIntegerList(ratingScale.filter((r) => target.includes(r)));
  } else {
    console.error(
      `Unable to build ratings string for ratings ${target} and ratings scale ${ratingScale}, with average ${average}`
    );
    return '';
  }
};

const buildPeriodString = (period: AutoCalibrationRulePeriod) => {
  return period.duration + ' ' + period.type;
};

const linearReplaceAll = (
  str: string,
  mapObj: Record<string, any>
): { result: string; remainingVariableNames: string[] } => {
  const remainingVariableNames: string[] = [];
  const result = str.replace(REGEX_VARIABLE, (matched, variable) => {
    const replacement = mapObj[variable];
    if (
      replacement === undefined ||
      replacement === null ||
      replacement === ''
    ) {
      remainingVariableNames.push(variable);
      return matched;
    }
    return String(replacement);
  });
  return { result, remainingVariableNames };
};

export const preComplileCalibrationRule = (
  rule: AutoCalibrationRule,
  ratingScale: RatingScale = [1, 2, 3, 4, 5],
  formatters: CalibrationPlaceholderFormatters = {}
): PreCompiledAutoCalibrationRule => {
  const condition = rule.condition as Record<string, any>;
  const values: Record<string, any> = {
    ...condition,
    duration: rule.period?.duration,
    period: rule.period ? buildPeriodString(rule.period) : '',
    target: buildRatingsString(condition.target, ratingScale),
  };
  if (formatters.direction && condition.direction) {
    values.direction = formatters.direction(condition.direction);
  }
  if (formatters.event && condition.event) {
    values.event = formatters.event(condition.event);
  }
  if (formatters.period && rule.period) {
    values.period = formatters.period(rule.period);
  }
  const {
    result: compiledTemplate,
    remainingVariableNames: compiledTemplateVariableNames,
  } = linearReplaceAll(rule.template, values);

  return {
    ...rule,
    compiledTemplate,
    compiledTemplateVariableNames,
    usesManagerPlaceholder:
      compiledTemplateVariableNames.includes(MANAGER_PLACEHOLDER),
  };
};

export const preComplileCalibrationRules = (
  rules: AutoCalibrationRule[],
  ratingScale: RatingScale = [1, 2, 3, 4, 5],
  formatters: CalibrationPlaceholderFormatters = {}
): PreCompiledAutoCalibrationRule[] =>
  rules.map((rule) =>
    preComplileCalibrationRule(rule, ratingScale, formatters)
  );

// Plain string replaces on purpose: this runs per dashboard row and per
// rule, and a regex is several times slower for the same result.
export const fillPersonPlaceholders = (
  rule: PreCompiledAutoCalibrationRule,
  names: CalibrationFlagNames
): string => {
  const text = rule.compiledTemplate.replace('{name}', names.person);
  return rule.usesManagerPlaceholder
    ? text.replace('{manager}', names.manager)
    : text;
};

// Rules that fired are filled with the row's names, the rest with generic
// ones for the "other calibration triggers" list. Both come from the
// caller so they can be localized.
export const getCalibrationFlags = (
  calibrationFlags: number[],
  rules: PreCompiledAutoCalibrationRule[],
  names: CalibrationFlagNames,
  genericNames: CalibrationFlagNames
) => {
  const flags: string[] = [];
  const missingFlags: string[] = [];
  for (let i = 0; i < rules.length; i++) {
    if (!isCalibrationRuleEnabled(rules[i])) {
      continue;
    }
    if (calibrationFlags.includes(i)) {
      flags.push(fillPersonPlaceholders(rules[i], names));
    } else {
      missingFlags.push(fillPersonPlaceholders(rules[i], genericNames));
    }
  }
  return [flags, missingFlags];
};
